/**
 * @module Support/Services
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
// core
import { Injectable } from '@angular/core';
import { FeatureToggleService } from '@core/feature-toggle/services/feature-toggle.service';
import { AppConfig } from '@root/app.config';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { APP_PERMISSION } from '../constants/device.constants';

@Injectable(
  { providedIn: 'root' }
)
export class FeatureToggleUtilService {
  /**
   * Virtual till feature store override
   */
  private virtualTillOverride: boolean;

  constructor(
    private featureToggleService: FeatureToggleService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private appConfig: AppConfig
  ) {
    const featureToggleConfig = appConfig.getConfig('featureToggleConfig');
    if (featureToggleConfig) {
      this.featureToggleService.setConfig(featureToggleConfig);
    }
    this.virtualTillOverride = null;
  }

  /**
   * Stores the feature flag to determine if the Captcha is enabled/disabled
   */
  public isCaptchaEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.CAPTCHA');
  }
  /**
   * Stores the feature flag to determine if the Invisible Captcha is enabled/disabled
   */
  public isInvisibleCaptchaEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.INVISIBLECAPTCHA');
  }
  /**
   * Stores the feature flag to determine if the fraud zero dollar plan is enabled/disabled
   */
  public isFraudZeroDollarPlanEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.FRAUDZERODOLLARPLAN');
  }
  /**
   * Stores the feature flag to determine if virtual till is enabled
   */
  public isVirtualTillEnabled(): boolean {
    if (this.virtualTillOverride === null) {
      return this.featureToggleService.isEnabled('FEATURE.VIRTUALTILL');
    }
    return this.virtualTillOverride;
  }

  /**
   * Stores the feature flag to determine if the receipt retrieval is enabled/disabled
   */
  public isReceiptRetrievalEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.RECEIPTRETRIEVAL');
  }

  /**
   * Stores the feature flag to determine if the PCI pause and resume is enabled/disabled
   */
  public isPCIPauseAndResumeEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.PCIPAUSEANDRESUME');
  }

  /**
   * Stores the feature flag to determine if virtual till override is enabled
   */
  public setVirtualTillOverride(isVirtualTillEnabled: boolean): void {
    this.virtualTillOverride = isVirtualTillEnabled;
  }
  /**
   * method to return permission when fraudLock
   * @param url
   * @param abf
   */
  public getFraudLockPermission(url: string, abf: string): boolean {
    const isFraudLock = this.stateStoreUtilsService.getAccountFraudStatus();
    if (isFraudLock) {
      return (
        this.stateStoreUtilsService.getPermissionABFValue(url, abf).toLocaleLowerCase() ===
        APP_PERMISSION.ENABLE
      );
    } else {
      return true;
    }
  }

  /**
   * method to check whether the DNS Feature FEATURE.DNSSETTING is enabled
   */
  public isDNSEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.DNSSETTING');
  }

  public isHazmatFeatureEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.HAZMATFLAG');
  }
  /**
   * method to check whether the change msisdn eligible Feature FEATURE.CHANGEMSISDNELIGIBLE is enabled
   */
  public isChangeMsisdnEligibleEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.CHANGEMSISDNELIGIBLE');
  }

  /**
   * method to check if One Time Pin (OTP) is enabled
   */
  public isOTPVerificationEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.OTPVERIFICATION');
  }

  /**
   * method to check whether manager override should be available on OTP/ZIP verification modal
   */
  public isManagerOverrideEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.MANAGEROVERRIDE');
  }

  /**
   * method to check whether manager override should be available on OTP/ZIP verification modal
   */
  public isLineSetupRefactorEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.LINESETUPREFACTOR');
  }

  /**
   * method to check whether the Enrollment Broadband Benefit Feature FEATURE.ENROLLMENTBROADBANDBENEFIT is enabled
   */
  public isEBBEnabled(): boolean {
    const EBB_FEATURE: string = 'FEATURE.ENROLLMENTBROADBANDBENEFIT';
    const expiredFeatures: string[] = this.featureToggleService.getExpiredFeatures() || [];
    const enableEbb: boolean = expiredFeatures.includes(EBB_FEATURE);
    return !enableEbb;
  }

  /**
   * method to check whether the Enrollment Broadband Benefit Feature FEATURE.ENROLLMENTBROADBANDBENEFIT is enabled
   */
  public isEsimEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.ESIMTESTDRIVE');
  }

  /**
   * method to check if One Time Pin (OTP) is enabled
   */
  public isExistingEmailOtpEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.EXISTINGEMAILOTPUSER');
  }

  /**
   * method to check whether the Enrollment Broadband Benefit Feature FEATURE.ESIMACTIVATIONS is enabled (sepearate from FEATURE.ESIMTESTDRIVE)
   */
  public isEsimActivationsEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.ESIMACTIVATIONS');
  }

  /**
   * method to check whether the PDP page new layout FEATURE.PDPENABLED is enabled
   */
  public isPDPEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.PDPENABLED');
  }
  public isNewProductSelectorTemplateEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.NEWPRODUCTSELECTORTEMPLATE');
  }
  public isDigitalCartTemplateEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.NEWCARTTEMPLATE');
  }

  public isZeroDollarServiceEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.SKIPZERODOLLARSERVICE');
  }

  public isNewHeaderFooterEnabled(): boolean {
    //  return true;
    return this.featureToggleService.isEnabled('FEATURE.NEWHEADERFOOTERENABLED');
  }
  /**
   * method to store value to show/hide the radio button for connect plans
   */
  public displayConnectPlansRadioButton(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.DISPLAYCONNECTPLANSRADIOBUTTON');
  }

  /**
   * method to check if COPPA is enabled
   */
  public isCoppaEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.COPPA');
  }

  public isSimProtectionEnabled(): boolean {
    return !!this.appConfig.getConfig('isSimProtectionEnabled');
  }

  /**
   * method to check if COPPA pdf download is enabled
   */
  public isCoppaPdfDownloadEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.DOWNLOADCOPPAPDF');
  }

  public isNewByodEnabled(): boolean {
    return this.featureToggleService.isEnabled('FEATURE.BYOD2023');
  }

  /**
   * method to check if DCC Price is enabled
   */
  public isDccPriceEnabled(): boolean {
    return this.appConfig.getConfig('waivedDCCPriceEnabled');
  }

  /**
   * method to check if TPOP is enabled
   */
  public isTPOPEnabled(): boolean {
    return !!this.appConfig.getConfig('isTpopEnabled');
  }
}
